import { Building2 } from "lucide-react";
import { useState } from "react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@/components/ui/DefaultSelectDropdown";
import { useSimplifiedUserData } from "@/providers/SimplifiedUserDataProvider";
import { useNavigate } from "react-router-dom";
import { useTabContext } from "@/contexts/TabContext";
import ContextMenu, { ContextMenuItem } from "@/components/ui/ContextMenu";

import { usePlatformTranslation } from '@/hooks/usePlatformTranslation';
interface CompanySwitcherProps {
  collapsed?: boolean;
}

export default function CompanySwitcher({ collapsed = false }: CompanySwitcherProps) {
  const { t } = usePlatformTranslation();
  const navigate = useNavigate();
  const { tabs, closeTab } = useTabContext();
  const [contextMenu, setContextMenu] = useState<{ isOpen: boolean; position: { x: number; y: number } }>({
    isOpen: false,
    position: { x: 0, y: 0 },
  });
  
  const {
    companies,
    selectedCompany,
    selectCompany,
  } = useSimplifiedUserData();

  // Don't render if user has only one company or no companies
  if (companies.length <= 1) {
    return null;
  }

  const handleCompanyChange = (companyId: string) => {
    // Close all open tabs when switching companies
    tabs.forEach(tab => {
      closeTab(tab.id);
    });
    
    // Set the selected company (store will automatically switch to a game from the new company)
    selectCompany(companyId);
    
    // Navigate to the homepage
    navigate('/home');
  };

  if (collapsed) {
    const menuItems: ContextMenuItem[] = companies.map((company) => ({
      id: company.id,
      label: company.name,
      onClick: () => handleCompanyChange(company.id),
      disabled: company.id === selectedCompany?.id,
    }));

    return (
      <>
        <button
          onClick={(e) => {
            const rect = e.currentTarget.getBoundingClientRect();
            setContextMenu({
              isOpen: true,
              position: {
                x: rect.right + 8,
                y: rect.top,
              },
            });
          }}
          className="flex items-center justify-center w-8 h-8 rounded-sm hover:bg-sidebar-accent hover:text-sidebar-accent-foreground transition-colors"
          title={`Current: ${selectedCompany?.name || 'No company'}\nClick to select company`}
        >
          <Building2 className="h-4 w-4 flex-shrink-0" />
        </button>
        <ContextMenu
          isOpen={contextMenu.isOpen}
          position={contextMenu.position}
          items={menuItems}
          onClose={() => setContextMenu({ ...contextMenu, isOpen: false })}
        />
      </>
    );
  }

  return (
    <div className="px-2 py-2">
      <Select
        value={selectedCompany?.id || ""}
        onValueChange={handleCompanyChange}
      >
        <SelectTrigger className="w-full">
          <div className="flex items-center gap-2 w-full">
            <Building2 className="h-4 w-4 flex-shrink-0" />
            <SelectValue placeholder={t('platform.ui.dropdowns.select_company')} />
          </div>
        </SelectTrigger>
        <SelectContent>
          {companies.map((company) => (
            <SelectItem key={company.id} value={company.id}>
              {company.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

